import React, { useEffect, useState } from "react";

import {
    Container,
    Box,
    Typography,
    Paper,
    Button,
    Alert,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import GavelIcon from "@mui/icons-material/Gavel";
import RuleIcon from "@mui/icons-material/Rule";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import EventNoteIcon from "@mui/icons-material/EventNote";
import GroupIcon from "@mui/icons-material/Group";
import ChecklistIcon from "@mui/icons-material/Check";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Footer from "../core/Footer";
import { createDownloadLink } from '../file/apiFiles';
import { isAuthenticated } from '../auth';

const Bullet = ({ children }) => (
    <ListItem sx={{ py: 0.5, alignItems: "flex-start" }}>
        <ListItemIcon sx={{ minWidth: 28, mt: "6px" }}>
            <FiberManualRecordIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
            primaryTypographyProps={{ variant: "body1", sx: { lineHeight: 1.7 } }}
            primary={children}
        />
    </ListItem>
);

const SubBullet = ({ children }) => (
    <ListItem sx={{ py: 0.25, pl: 5, alignItems: "flex-start" }}>
        <ListItemIcon sx={{ minWidth: 24, mt: "6px" }}>
            <FiberManualRecordIcon sx={{ fontSize: 10 }} />
        </ListItemIcon>
        <ListItemText
            primaryTypographyProps={{ variant: "body1", sx: { lineHeight: 1.7 } }}
            primary={children}
        />
    </ListItem>
);

const SectionTitle = ({ icon, title, chip }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        {icon}
        <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#003E7E" }}>
                    {title}
                </Typography>
                {chip ? <Chip size="small" label={chip} /> : null}
            </Box>
        </Box>
    </Box>
);

const Charter = () => {

    const token = isAuthenticated() && isAuthenticated().token;

    const fileInfo = {
        fileName: "001-0004_SDS_Industry_Consortium_Charter_rev5.pdf.pdf",
        awsVersion: "3XDLUBniHIF0tiJ40ls4rrdHyJmUjkO3",
    };

    const [pdfUrl, setPdfUrl] = useState("");

    useEffect(() => {
        let cancelled = false;

        async function loadUrl() {
            if (!token) return; // guard

            try {
                const data = await createDownloadLink(token, fileInfo);
                if (data?.error) {
                    console.log(data.error);
                    return;
                }

                const url = data?.url || data?.signedUrl || data;
                if (!cancelled) setPdfUrl(url);
            } catch (e) {
                console.log(e);
            }
        }

        loadUrl();
        return () => { cancelled = true; };
    }, [token]); // important


    return (
        <>
            {/* Hero Section */}
            <Box sx={{ backgroundColor: "#003E7E", color: "white", py: 5, mb: 4 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <DescriptionIcon sx={{ fontSize: 56 }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                                SDSIC Charter
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                Substation Design Solution (SDS) Industry Consortium Charter — Revision 5 (11/2022)
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            href={pdfUrl || undefined}
                            disabled={!pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                backgroundColor: "#FF6600",
                                "&:hover": { backgroundColor: "#e55a00" },
                            }}
                        >
                            {"Download PDF"}
                        </Button>

                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Industry Consortium Standard Position Statement • Document 001-0004
                        </Typography>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ mb: 6 }}>
                {/* Charter Content */}
                <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, mb: 4, borderRadius: 3 }}>
                    <SectionTitle
                        icon={<GavelIcon sx={{ fontSize: 40, color: "#003E7E" }} />}
                        title="Scope"
                    />

                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: "1.05rem" }}>
                        SDS Industry Consortium is made up of member utilities and contract design/engineering firms
                        that have adopted or are investigating new technologies or workflows to improve design,
                        engineering, field construction, operations and maintenance of utility projects. The
                        Consortium will meet, as needed, to establish common workflows, common software
                        functionality, shared libraries, common standards, technology needs, and any other topics that
                        help drive our vendor&apos;s software development efforts according to an industry standard. The
                        SDS Industry Consortium will drive standards to promote common library development from
                        internal resources, external partners, and/or equipment manufacturers.
                    </Typography>

                    <Alert
                        severity="warning"
                        sx={{
                            mb: 2.5,
                            "& .MuiAlert-icon": { fontSize: 28 },
                            fontSize: "1rem",
                        }}
                    >
                        Solicitation of member utilities or individual members of the SDS Industry Consortium by
                        engineering contractors, Software Vendors, Training providers, and/or manufacturers is{" "}
                        <strong>STRICTLY PROHIBITED</strong>. The purpose of the SDS Industry Consortium is to
                        establish best practices with each member&apos;s responsibility to bring value. This is NOT a
                        sales meeting.
                    </Alert>

                    <Alert
                        severity="info"
                        icon={<InfoIcon />}
                        sx={{
                            "& .MuiAlert-icon": { fontSize: 28 },
                            fontSize: "1rem",
                        }}
                    >
                        <strong>Note:</strong> Among general membership, only utility employees have voting
                        privileges and must be a registered committee member to vote in committee elections. All
                        members holding Co-Chair roles have voting privileges regardless of company affiliation.
                    </Alert>

                    <Divider sx={{ my: 4 }} />

                    <SectionTitle
                        icon={<RuleIcon sx={{ fontSize: 38, color: "#003E7E" }} />}
                        title="Operating Guidelines"
                    />

                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "#003E7E" }}>
                        Organizational Structure
                    </Typography>

                    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
                        Consortium Leadership Council positions do not have term limits but are elected/re-elected
                        for a term spanning two in-person Consortium meetings. Officer elections will occur at the
                        Annual in-person Consortium meeting as follows: Vice-Chair &amp; Co-Chair positions will be
                        elected in odd years and Chair positions will be elected in even years. In the event that a
                        current Co-Chair is elected as the new Chair, the Consortium Chair and Vice Chair will
                        appoint a Co-Chair replacement for the remainder of the term. In the event of a loss of
                        knowledge and continuity due to majority election changeover, the Consortium Chair and/or
                        Vice Chair have the right to extend an officer&apos;s tenure in increments of election terms
                        until operations can be stabilized. The goal is to maintain momentum through the election
                        period to ensure a seamless transition. Council Chair/Co-Chairs should not be from the same
                        Utility without prior authorization from the Leadership Council Chair &amp; Vice-Chair.
                    </Typography>

                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "#003E7E" }}>
                        Consortium Leadership Council
                    </Typography>

                    <List dense disablePadding>
                        <Bullet>
                            <strong>Utility Only (voting positions):</strong>
                        </Bullet>
                        <SubBullet>Consortium Chair</SubBullet>
                        <SubBullet>Consortium Vice Chair</SubBullet>
                        <SubBullet>Electrical Chair</SubBullet>
                        <SubBullet>Business Strategy Chair</SubBullet>
                        <SubBullet>Physical Chair</SubBullet>
                        <SubBullet>Civil/Structural Chair</SubBullet>
                        <SubBullet>Technology Chair</SubBullet>
                        <SubBullet>Innovations Chair</SubBullet>

                        <Bullet>
                            <strong>Utility OR Engineering and Design Contract firms (voting positions):</strong>
                        </Bullet>
                        <SubBullet>Electrical Co-Chair</SubBullet>
                        <SubBullet>Business Strategy Co-Chair</SubBullet>
                        <SubBullet>Physical Co-Chair</SubBullet>
                        <SubBullet>Civil/Structural Co-Chair</SubBullet>
                        <SubBullet>Technology Co-Chair</SubBullet>
                        <SubBullet>Innovations Co-Chair</SubBullet>

                        <Bullet>
                            <strong>Utility/Software Vendor/Contract Design/etc. (non-voting positions):</strong>
                        </Bullet>
                        <SubBullet>
                            Representative from each major software developer as determined by Leadership Chair and
                            Vice-Chair
                        </SubBullet>
                        <SubBullet>Documentation specialist</SubBullet>
                        <SubBullet>Newsletter editor</SubBullet>
                        <SubBullet>Advisory Position (Engineering/Design Firm)</SubBullet>
                        <SubBullet>Advisory Position (Engineering/Design Firm)</SubBullet>
                        <SubBullet>Advisory Position (Manufacturer)</SubBullet>
                    </List>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.7 }}>
                        <strong>Note:</strong> A major software developer is defined as any technology provider that
                        is developing or has already developed a technology that focuses on helping the Utility
                        Industry improve. Software developers must demonstrate a willingness to support SDSIC
                        members by working closely with committees that use their solutions.
                    </Typography>

                    <List dense disablePadding sx={{ mt: 2 }}>
                        <Bullet>
                            If Committee Chair is unable to fulfill his/her duties (either by resignation or council
                            evaluation), the Co-Chair will take over his/her duties providing this individual is a
                            utility employee. The Consortium Leadership Chair and Vice-Chair will appoint a Co-Chair
                            replacement. If the current Co-Chair is an engineering/design firm representative, the
                            Chair position will remain open until a replacement can be put in place by the Consortium
                            Leadership Chair and Vice-Chair.
                        </Bullet>
                        <Bullet>
                            When a non-utility member fills a co-chair role mid-term, they will fill out that term and
                            are eligible to run again during the next election. They will not carry over to fill a full
                            term as they would in a co-chair utility employee role. This is done to ensure that all
                            non-utility co-chair roles are elected to that position by their utility peers.
                        </Bullet>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <SectionTitle
                        icon={<EventNoteIcon sx={{ fontSize: 38, color: "#003E7E" }} />}
                        title="Operations"
                    />

                    <List dense disablePadding>
                        <Bullet>Consortium chairperson will facilitate the consortium.</Bullet>
                        <Bullet>
                            Monthly working sessions among member utilities, with a face-to-face meeting at least once
                            a year.
                        </Bullet>
                        <Bullet>Minutes will be taken during the consortium and posted within two weeks of meeting.</Bullet>
                        <Bullet>The SDS Industry Consortium Council will establish the agenda for each consortium meeting.</Bullet>
                        <Bullet>
                            Consortium topics will be brought to the discipline Chairpersons first and then to the
                            Consortium Council as a whole. Topics will be prioritized by the council first.
                        </Bullet>
                        <Bullet>
                            The highest priority topics will be facilitated by the discipline Chairpersons in the
                            discipline meetings with the goal of gathering data and bringing it back to the Council to
                            move forward.
                        </Bullet>
                        <Bullet>
                            If the Council cannot reach concurrence on priority, the Consortium Chair and Vice Chair
                            will assign the final priority.
                        </Bullet>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <SectionTitle
                        icon={<GroupIcon sx={{ fontSize: 38, color: "#003E7E" }} />}
                        title="Roles"
                    />

                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "#003E7E" }}>
                        Role of Chairpersons
                    </Typography>
                    <List dense disablePadding>
                        <Bullet>Keep Council abreast of chairperson&apos;s discipline activities.</Bullet>
                        <Bullet>Schedule meetings in advance so attendance can be planned by all consortium members.</Bullet>
                        <Bullet>
                            Facilitate prioritized topics (Top 5 per discipline) in discipline meetings for data
                            gathering and communicate findings up to Council.
                        </Bullet>
                        <Bullet>
                            Serve as point of contact for issues/questions/requests coming from members or potential
                            members.
                        </Bullet>
                        <Bullet>
                            Ensure SDS Industry Consortium website stays up to date with meeting minutes, action log,
                            working agendas, and other information useful to members.
                        </Bullet>
                        <Bullet>Ensure action items are being proactively addressed.</Bullet>
                    </List>

                    <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1.5, color: "#003E7E" }}>
                        Role of Members
                    </Typography>
                    <List dense disablePadding>
                        <Bullet>Attend all team meetings as scheduled.</Bullet>
                        <Bullet>Willingly accept assignments and complete by date requested.</Bullet>
                        <Bullet>Do required work/research before meetings to maximize effectiveness of team meeting time.</Bullet>
                        <Bullet>Share summary info after attending industry training, meetings, seminars.</Bullet>
                        <Bullet>
                            Proactively share ideas on design tools, work processes/procedures, design tool
                            efficiencies, new tools needed, training identified, etc.
                        </Bullet>
                        <Bullet>
                            Please reference document &quot;(003-0001) SDS Industry Consortium Membership Guidelines&quot;
                            for additional information.
                        </Bullet>
                    </List>

                    <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1.5, color: "#003E7E" }}>
                        Role of Software Development Partners
                    </Typography>
                    <List dense disablePadding>
                        <Bullet>
                            Software Development Partners respond to consortium requests, share ideas on design tools,
                            new functionality, direction of new development, etc.
                        </Bullet>
                        <Bullet>
                            Software Developments Partners cannot hold a Co-Chair role in a designated committee.
                        </Bullet>
                        <Bullet>
                            In order to hold an advisory position on the leadership council a Software Development
                            Partner must meet the following criteria:
                        </Bullet>
                        <SubBullet>Must be actively involved for a minimum of two years.</SubBullet>
                        <SubBullet>
                            Must be nominated for an advisor role by a current leadership Chair, Vice-Chair or
                            Committee Chair/co-chair.
                        </SubBullet>
                        <SubBullet>Must be voted in by the Leadership Council.</SubBullet>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <SectionTitle
                        icon={<ChecklistIcon sx={{ fontSize: 38, color: "#003E7E" }} />}
                        title="Common Agenda Items"
                    />

                    <List dense disablePadding>
                        <Bullet>Common Standards (ex. Naming conventions, library development, etc.).</Bullet>
                        <Bullet>New technology, toolkits, etc.</Bullet>
                        <Bullet>Common workflows.</Bullet>
                        <Bullet>Emerging needs.</Bullet>
                        <Bullet>Response to requests.</Bullet>
                        <Bullet>Establish future meeting logistics (dates/locations).</Bullet>
                        <Bullet>Issue resolution topics.</Bullet>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <SectionTitle
                        icon={<AccountTreeIcon sx={{ fontSize: 38, color: "#003E7E" }} />}
                        title="SDSIC Structure Flow Diagram"
                        chip="Text extracted from diagram"
                    />

                    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8 }}>
                        SDSIC STRUCTURE FLOW DIAGRAM:
                    </Typography>

                    <List dense disablePadding>
                        <Bullet>
                            <strong>Leadership Council</strong> (Chair/Vice-Chair)
                        </Bullet>
                        <SubBullet>Software Development Partners</SubBullet>
                        <SubBullet>Advisors (3)</SubBullet>
                        <Bullet>
                            Under Leadership Council: <strong>Electrical</strong>, <strong>Business Strategy</strong>,{" "}
                            <strong>Civil/Structural</strong>, <strong>Physical</strong>, <strong>Technology</strong>,{" "}
                            <strong>Innovations</strong> (each with Chair/Co-Chair)
                        </Bullet>
                        <Bullet>
                            <strong>Membership</strong>
                        </Bullet>
                        <Bullet>
                            <strong>Additional Roles:</strong> Documentation Specialist; Newsletter Editor; Technical
                            Advisor / Points of contact
                        </Bullet>
                        <Bullet>
                            Legend: <strong>Voting</strong> and <strong>Non-Voting</strong>
                        </Bullet>
                    </List>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Business Use
                    </Typography>

                    <Divider sx={{ my: 4 }} />

                    <SectionTitle
                        icon={<HistoryEduIcon sx={{ fontSize: 38, color: "#003E7E" }} />}
                        title="Revision History"
                    />

                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                        <Table size="small" aria-label="revision history table">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Rev. #</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Rev. Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Changes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>0</TableCell>
                                    <TableCell>October 6, 2014</TableCell>
                                    <TableCell>
                                        Initial creation of document. (Michael Goins, Duke Energy)
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>1</TableCell>
                                    <TableCell>January 14, 2016</TableCell>
                                    <TableCell>
                                        1) Section: Scope. Extend membership to include Contract Design / Engineering Firms
                                        as non-voting participants. 2) Section: Organizational Structure. Added verbiage to
                                        define election cycles. 3) Section: Organizational Structure and Operations. Changed
                                        all reference of Co-Chair to Vice-Chair. 4) Added revision block. (John D. Thomas,
                                        Southern Company) 5) Added Civil3D Chair and Vice-Chair, Newsletter editor
                                        (non-voting), &amp; Event coordinator editor (non-voting), to the hierarchy (Jeremy
                                        Morton, Duke Energy) 6) Added verbiage about non solicitation from Contract
                                        Design/Engineering under the Roles of Members section (Jeremy Morton, Duke Energy)
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>2</TableCell>
                                    <TableCell>October 3, 2018</TableCell>
                                    <TableCell>
                                        Updated items to better reflect the current structure of the SDSIC. Still need to
                                        update the SDSIC Structure Flow Diagram.
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>2A</TableCell>
                                    <TableCell>December 13, 2018</TableCell>
                                    <TableCell>Updated SDSIC Structure Flow Diagram</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>3</TableCell>
                                    <TableCell>August 8, 2019</TableCell>
                                    <TableCell>
                                        Revised officer election year due to transition from bi-annual to annual face to face
                                        meetings.
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>4</TableCell>
                                    <TableCell>August 12, 2021</TableCell>
                                    <TableCell>
                                        Revised to allow Committee Co-Chair roles to be held by an Engineering/Design Firm
                                        employee. Changes include voting privileges for Co-Chairs.
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>5</TableCell>
                                    <TableCell>November 2022</TableCell>
                                    <TableCell>
                                        Revised to reflect inclusion of additional software developers into the organization.
                                        Also modified to give committee co-chairs voting privileges.
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Divider sx={{ my: 4 }} />


            </Container>

            <Footer />
        </>
    );
};

export default Charter;
